import { useState, useEffect, useRef } from "react";
import { theme } from "../../theme";

/**
 * ScrollHint — visual indicator that more content is available below.
 * Shows gradient overlay + animated chevron above BottomNav.
 * Uses pointer-events: none so clicks pass through.
 */
export default function ScrollHint({ bottomOffset = 68 }) {
  const [visible, setVisible] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    const check = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const remaining = docHeight - scrollTop - windowHeight;
      setVisible(remaining > 80);
    };

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(check);
    };

    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", check, { passive: true });

    // Observe DOM changes (lazy-loaded content)
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: false });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", check);
      obs.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes scrollBounce {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(5px); opacity: 1; }
        }
      `}</style>
      <div style={{
        position: "fixed",
        bottom: bottomOffset,
        left: "50%",
        transform: "translateX(-50%)",
        width: "100%",
        maxWidth: 480,
        pointerEvents: "none",
        zIndex: 90,
      }}>
        <div style={{
          height: 48,
          background: `linear-gradient(to bottom, transparent, ${theme.bg}ee)`,
        }} />
        <div style={{
          textAlign: "center",
          background: `${theme.bg}ee`,
          paddingBottom: 6,
        }}>
          <span style={{
            display: "inline-block",
            fontSize: 16,
            color: theme.textDim,
            animation: "scrollBounce 2s ease-in-out infinite",
          }}>
            ⌄
          </span>
        </div>
      </div>
    </>
  );
}
